import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { Brand, LangToggle } from "@/components/Brand";
import { SiteFooter } from "@/components/SiteFooter";

/** Shared chrome for the corporate and legal pages. */
export const PageLayout = ({ eyebrow, title, intro, updated, children, testId }) => {
  const { t } = useI18n();

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-[#F3F3F1] grain flex flex-col" data-testid={testId}>
      <header className="glass sticky top-0 z-40 border-b border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-12 h-16 flex items-center justify-between gap-4">
          <Brand />
          <div className="flex items-center gap-3">
            <LangToggle />
            <Link to="/" data-testid="back-home-link"
              className="inline-flex items-center gap-2 min-h-[44px] text-[11px] tracking-[0.18em] uppercase text-[#A8A8A8] hover:text-[#D4AF37] transition-colors">
              <ArrowLeft size={14} /> <span className="hidden sm:inline">{t("back_home")}</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-4xl w-full mx-auto px-5 lg:px-12 py-16 lg:py-24">
        <p className="eyebrow mb-5">{eyebrow}</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.05]">{title}</h1>
        {intro && <p className="mt-7 text-base sm:text-lg text-[#A8A8A8] leading-relaxed max-w-2xl">{intro}</p>}
        {updated && (
          <p className="mt-6 font-mono-accent text-[10px] tracking-[0.18em] uppercase text-[#707070]"
            data-testid="last-updated">
            {t("last_updated")}: {updated}
          </p>
        )}
        <div className="mt-14">{children}</div>
      </main>

      <SiteFooter />
    </div>
  );
};

/** One numbered clause block within a legal document. */
export const Clause = ({ n, title, children }) => (
  <section className="border-t border-[#1E1E1E] pt-9 mt-9 first:border-0 first:pt-0 first:mt-0"
    data-testid={`clause-${n}`}>
    <div className="flex gap-5">
      <span className="font-mono-accent text-xs text-[#D4AF37] pt-1.5 shrink-0">{String(n).padStart(2, "0")}</span>
      <div className="min-w-0">
        <h2 className="text-xl sm:text-2xl tracking-tight">{title}</h2>
        <div className="mt-5 space-y-4 text-sm sm:text-base text-[#A8A8A8] leading-relaxed">{children}</div>
      </div>
    </div>
  </section>
);

/** Pulled-out statement for the clauses that carry real legal weight. */
export const Callout = ({ children, tone = "gold" }) => (
  <p className={`border-l-2 pl-5 py-1 text-[#F3F3F1] ${tone === "red" ? "border-[#8B0000]" : "border-[#D4AF37]"}`}>
    {children}
  </p>
);

export const Bullets = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map((i) => (
      <li key={i} className="flex gap-3">
        <span className="text-[#D4AF37] shrink-0">·</span>
        <span>{i}</span>
      </li>
    ))}
  </ul>
);
